import { useState, useEffect, useCallback } from 'react';
import {
  FileText, Loader2, Eye, EyeOff, Plus, Trash2, PencilLine, Wand2,
  ExternalLink, Copy,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast, Toaster } from 'sonner';
import api from '@/services/api';

const CANONICAL = ['return-policy', 'terms', 'privacy'];

const StatusBadge = ({ status }) => {
  const tone = status === 'published' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-900';
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${tone}`}>{status}</span>;
};

export default function LegalPagesPage() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [seedOpen, setSeedOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/hq/legal-pages');
      setPages(res.data.items || []);
    } catch (e) {
      toast.error('Failed to load pages', { description: e.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditing({ slug: '', title: '', html: '', meta_description: '', status: 'draft', _isNew: true });
    setEditorOpen(true);
  };

  const openEdit = (p) => {
    setEditing({ ...p, _isNew: false });
    setEditorOpen(true);
  };

  const save = async () => {
    if (!editing) return;
    if (!editing.slug?.trim() || !editing.title?.trim() || !editing.html?.trim()) {
      toast.error('Slug, title, and body are required'); return;
    }
    setSaving(true);
    try {
      if (editing._isNew) {
        await api.post('/hq/legal-pages', {
          slug: editing.slug.trim().toLowerCase(),
          title: editing.title.trim(),
          html: editing.html,
          status: editing.status,
          meta_description: editing.meta_description || '',
        });
      } else {
        await api.patch(`/hq/legal-pages/${editing.slug}`, {
          title: editing.title.trim(),
          html: editing.html,
          status: editing.status,
          meta_description: editing.meta_description || '',
        });
      }
      toast.success('Page saved');
      setEditorOpen(false); setEditing(null);
      await load();
    } catch (e) {
      toast.error('Save failed', { description: e.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  };

  const togglePublish = async (p) => {
    try {
      await api.patch(`/hq/legal-pages/${p.slug}`, {
        status: p.status === 'published' ? 'draft' : 'published',
      });
      await load();
    } catch (e) {
      toast.error('Failed', { description: e.response?.data?.detail || e.message });
    }
  };

  const remove = async () => {
    if (!deleting) return;
    try {
      await api.delete(`/hq/legal-pages/${deleting.slug}`);
      toast.success('Page deleted');
      setDeleting(null);
      await load();
    } catch (e) {
      toast.error('Delete failed', { description: e.response?.data?.detail || e.message });
    }
  };

  const seedDefaults = async (replace) => {
    setSeeding(true);
    try {
      const res = await api.post(`/hq/legal-pages/seed-defaults?replace=${replace}`);
      toast.success(`Seeded ${res.data.inserted?.length || 0} pages`, {
        description: res.data.skipped?.length ? `Skipped: ${res.data.skipped.join(', ')}` : undefined,
      });
      setSeedOpen(false);
      await load();
    } catch (e) {
      toast.error('Seed failed', { description: e.response?.data?.detail || e.message });
    } finally { setSeeding(false); }
  };

  const copyUrl = (slug) => {
    const url = `${window.location.origin}/api/ecom/pages/${slug}`;
    navigator.clipboard.writeText(url);
    toast.success('URL copied', { description: url });
  };

  return (
    <div className="p-6 space-y-6" data-testid="legal-pages-page">
      <Toaster richColors position="top-right" />

      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Legal Pages</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage Return &amp; Cancellation, Terms of Use, Privacy Policy and any custom pages. Content is served live to the storefront via
            <code className="text-xs bg-slate-100 rounded px-1 py-0.5 ml-1">/api/ecom/pages/&#123;slug&#125;</code>.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setSeedOpen(true)} data-testid="seed-btn">
            <Wand2 className="h-4 w-4 mr-2" /> Seed defaults
          </Button>
          <Button variant="outline" asChild data-testid="preview-json-btn">
            <a href="/api/ecom/pages" target="_blank" rel="noreferrer"><ExternalLink className="h-4 w-4 mr-2" /> Preview JSON</a>
          </Button>
          <Button onClick={openNew} data-testid="new-page-btn"><Plus className="h-4 w-4 mr-2" /> New page</Button>
        </div>
      </div>

      {loading ? (
        <div className="p-12 text-center text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" /> Loading…</div>
      ) : pages.length === 0 ? (
        <Card><CardContent className="p-12 text-center">
          <FileText className="h-10 w-10 mx-auto mb-3 text-muted-foreground opacity-50" />
          <div className="font-medium">No legal pages yet</div>
          <div className="text-sm text-muted-foreground mt-1 mb-4">Seed the three defaults to get started.</div>
          <Button onClick={() => setSeedOpen(true)} data-testid="empty-seed-btn"><Wand2 className="h-4 w-4 mr-2" /> Seed defaults</Button>
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {pages.map(p => (
            <Card key={p.slug} data-testid={`page-row-${p.slug}`}>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-10 h-10 rounded-md bg-slate-100 flex items-center justify-center">
                  <FileText className="h-5 w-5 text-slate-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium">{p.title}</span>
                    <StatusBadge status={p.status} />
                    {CANONICAL.includes(p.slug) && (
                      <span className="text-xs px-1.5 py-0.5 bg-slate-100 rounded-full text-slate-600">core</span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    /{p.slug} · updated {new Date(p.updated_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="ghost" onClick={() => copyUrl(p.slug)} data-testid={`copy-url-${p.slug}`} title="Copy public URL"><Copy className="h-4 w-4" /></Button>
                  <Button size="sm" variant="outline" onClick={() => togglePublish(p)} data-testid={`toggle-publish-${p.slug}`}>
                    {p.status === 'published' ? <><EyeOff className="h-4 w-4 mr-1" /> Unpublish</> : <><Eye className="h-4 w-4 mr-1" /> Publish</>}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => openEdit(p)} data-testid={`edit-${p.slug}`}><PencilLine className="h-4 w-4" /></Button>
                  {!CANONICAL.includes(p.slug) && (
                    <Button size="sm" variant="outline" onClick={() => setDeleting(p)} data-testid={`delete-${p.slug}`}><Trash2 className="h-4 w-4" /></Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Editor dialog */}
      <Dialog open={editorOpen} onOpenChange={(v) => { if (!v) { setEditorOpen(false); setEditing(null); setPreviewHtml(false); } }}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="editor-dialog">
          <DialogHeader>
            <DialogTitle>{editing?._isNew ? 'New legal page' : `Edit — ${editing?.title}`}</DialogTitle>
            <DialogDescription>Dangerous HTML (script/style/iframe) is stripped automatically on save.</DialogDescription>
          </DialogHeader>

          {editing && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Slug (URL path)</Label>
                  <Input
                    value={editing.slug}
                    onChange={(e) => setEditing({ ...editing, slug: e.target.value.toLowerCase() })}
                    disabled={!editing._isNew}
                    placeholder="e.g. shipping-policy"
                    data-testid="editor-slug"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Public URL: <code>/api/ecom/pages/{editing.slug || '…'}</code></p>
                </div>
                <div>
                  <Label className="text-xs">Status</Label>
                  <select
                    value={editing.status}
                    onChange={(e) => setEditing({ ...editing, status: e.target.value })}
                    className="w-full h-10 rounded-md border px-3 text-sm"
                    data-testid="editor-status"
                  >
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                  </select>
                </div>
              </div>

              <div>
                <Label className="text-xs">Title</Label>
                <Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="editor-title" />
              </div>

              <div>
                <Label className="text-xs">Meta description (SEO)</Label>
                <Textarea rows={2} value={editing.meta_description || ''} onChange={(e) => setEditing({ ...editing, meta_description: e.target.value })} data-testid="editor-meta" />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-xs">Body (HTML — safe tags only)</Label>
                  <button
                    type="button"
                    onClick={() => setPreviewHtml(v => !v)}
                    className="text-xs underline text-slate-600 hover:text-slate-900"
                    data-testid="toggle-preview"
                  >
                    {previewHtml ? 'Edit HTML' : 'Preview'}
                  </button>
                </div>
                {previewHtml ? (
                  <div
                    className="prose prose-slate max-w-none border rounded-md p-4 min-h-[300px] max-h-[400px] overflow-y-auto bg-white"
                    dangerouslySetInnerHTML={{ __html: editing.html || '<p class="text-muted-foreground">Nothing to preview</p>' }}
                    data-testid="editor-preview"
                  />
                ) : (
                  <Textarea
                    rows={16}
                    className="font-mono text-xs"
                    value={editing.html}
                    onChange={(e) => setEditing({ ...editing, html: e.target.value })}
                    data-testid="editor-html"
                  />
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  Allowed tags: <code>p, h2, h3, ul, ol, li, strong, em, a, blockquote, table</code>. Everything else is stripped on save.
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={() => { setEditorOpen(false); setEditing(null); setPreviewHtml(false); }}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="editor-save-btn">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}>
        <AlertDialogContent data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{deleting?.title}"?</AlertDialogTitle>
            <AlertDialogDescription>The page will disappear from the storefront. Soft-delete — reach out to engineering to restore.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-delete-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Seed defaults */}
      <AlertDialog open={seedOpen} onOpenChange={setSeedOpen}>
        <AlertDialogContent data-testid="seed-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Seed default legal pages</AlertDialogTitle>
            <AlertDialogDescription>
              Inserts three pre-written pages: Return &amp; Cancellation Policy, Terms of Use, Privacy Policy.
              {pages.length > 0 && (
                <span className="block mt-2 text-amber-700 font-medium">
                  ⚠️ Choose "Replace" to overwrite the current versions, otherwise existing slugs will be skipped.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 flex-wrap sm:flex-nowrap">
            <AlertDialogCancel disabled={seeding}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => seedDefaults(false)} disabled={seeding} data-testid="seed-append-btn">
              {seeding && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Seed missing only
            </AlertDialogAction>
            {pages.length > 0 && (
              <AlertDialogAction onClick={() => seedDefaults(true)} disabled={seeding} className="bg-amber-600 hover:bg-amber-700" data-testid="seed-replace-btn">
                {seeding && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Replace all
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
